import { plainText, requiredValidator, url } from '../../../../../../../../modules/validators';
import { FormVmItemConfigType, InitialData } from '../../../../../../../../infrastructure/form-viewmodel-controller';
import { trimSpaces, trimSpacesLeft } from '../../../../../../../../modules/formatters';
import { buildConfigList } from '../../../../../../../../infrastructure/utils/form-config.utils';
import { AudioBridgeProvider } from '../../../../../../../../api/connections.dao';
import { AudioBridgeConnectionField as FieldKey } from './audio-bridge-connection.form-config';
import { AudioBridgeProviderLabel } from '../../../../../../../../infrastructure/utils/common.utils';

export enum AudioBridgeConnectionField {
    LABEL = 'label',
    AUDIO_PROVIDER_ID = 'audioProviderId',
    AUDIO_PROVIDER_NAME = 'audioProviderName',
    PROVIDER = 'provider',
    PARTICIPANT_TELEPHONE_NUMBERS = 'participantTelephoneNumbers',
    PARTICIPANT_ONE_TAP_DIAL = 'participantOneTapDial',
    CONFERENCE_ID = 'conferenceId',
    PARTICIPANT_ID = 'participantId',
    PASSCODE = 'connectionPassword',
    OPERATOR_TELEPHONE_NUMBER = 'operatorTelephoneNumber',
    OPERATOR_PIN = 'operatorPin',
    OPERATOR_CONSOLE_LINK = 'hostLink',
    CONNECTION_NOTES = 'connectionNotes',
    AUDIO_BRIDGE_ID = 'audioBridgeID',
    AUDIO_BRIDGE_NAME = 'audioBridgeName',
    MEETING_UNIQUE_REFERENCE = 'meetingUniqueReference',
    AUTO_SYNC_ENABLED = 'autoSyncEnabled',
    AUTOMATICALLY_RECORD_ENABLED = 'automaticallyRecordEnabled',
}

export const customAudioProviderOption = {
    value: 'CUSTOM_AUDIO_PROVIDER',
    label: 'Custom Participant Telephone Numbers'
};

export const TelephoneNumberProvider = {
    OTHER: AudioBridgeProviderLabel[AudioBridgeProvider.OTHER],
    ZOOM: AudioBridgeProviderLabel[AudioBridgeProvider.ZOOM],
    TURBOBRIDGE: AudioBridgeProviderLabel[AudioBridgeProvider.TURBOBRIDGE],
};

export const participantTelephoneNumbersRequiredValidator = (value: string = '', formValues: InitialData) => {
    if (formValues[FieldKey.AUDIO_PROVIDER_ID] !== customAudioProviderOption.value) {
        return { valid: true, validationMessage: null };
    } else {
        const val = value?.replace(/\s/g, '') || '';
        return requiredValidator(val);
    }
};

export const audioProviderRequiredValidator = (value: string = '') => {
    const val = value ? value.toString() : '';
    return requiredValidator(val);
};

export const FORM_CONFIG = {
    [AudioBridgeConnectionField.LABEL]: {
        labelDescriptor: { id: 'oe.connection.label', defaultMessage: 'Label', description: 'Label text' },
        defaultValue: '',
        validators: [plainText.maxLength.short],
    },
    [AudioBridgeConnectionField.AUDIO_PROVIDER_ID]: {
        labelDescriptor: { id: 'oe.connection.telephoneNumberSet', defaultMessage: 'Telephone Number Set', description: 'Label text' },
        validators: [audioProviderRequiredValidator],
        defaultValue: '',
    },
    [AudioBridgeConnectionField.AUDIO_PROVIDER_NAME]: {
        defaultValue: '',
    },
    [AudioBridgeConnectionField.PROVIDER]: {
        labelDescriptor: { id: 'oe.connection.provider', defaultMessage: 'Provider', description: 'Label text' },
        defaultValue: '',
    },
    [AudioBridgeConnectionField.AUDIO_BRIDGE_NAME]: {
        labelDescriptor: { id: 'oe.connection.audioBridgeName', defaultMessage: 'Audio Bridge Name', description: 'Label text' },
        defaultValue: '',
        validators: [plainText.maxLength.short, requiredValidator],
    },
    [AudioBridgeConnectionField.AUDIO_BRIDGE_ID]: {
        labelDescriptor: { id: 'oe.connection.audioBridgeID', defaultMessage: 'Audio Bridge ID', description: 'Label text' },
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft],
        defaultValue: ''
    },
    [AudioBridgeConnectionField.PARTICIPANT_TELEPHONE_NUMBERS]: {
        labelDescriptor: { id: 'oe.connection.participantTelephoneNumbers', defaultMessage: 'Participant Telephone Numbers', description: 'Label text' },
        validators: [participantTelephoneNumbersRequiredValidator, plainText.maxLength.long],
        formatters: [trimSpacesLeft],
        defaultValue: ''
    },
    [AudioBridgeConnectionField.PARTICIPANT_ONE_TAP_DIAL]: {
        labelDescriptor: { id: 'oe.connection.participantOneTapDial', defaultMessage: 'Participant One-Tap Dial', description: 'Label text' },
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft],
        defaultValue: ''
    },
    [AudioBridgeConnectionField.CONFERENCE_ID]: {
        labelDescriptor: { id: 'oe.connection.conferenceId', defaultMessage: 'Conference ID', description: 'Label text' },
        validators: [plainText.maxLength.short, requiredValidator],
        formatters: [trimSpacesLeft],
        defaultValue: ''
    },
    [AudioBridgeConnectionField.PARTICIPANT_ID]: {
        labelDescriptor: { id: 'oe.connection.participantId', defaultMessage: 'Participant ID', description: 'Label text' },
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft],
        defaultValue: ''
    },
    [AudioBridgeConnectionField.PASSCODE]: {
        labelDescriptor: { id: 'oe.connection.passcode', defaultMessage: 'Passcode', description: 'Label text' },
        validators: [plainText.maxLength.short],
        formatters: [trimSpaces],
        defaultValue: ''
    },
    [AudioBridgeConnectionField.OPERATOR_TELEPHONE_NUMBER]: {
        labelDescriptor: { id: 'oe.connection.operatorTelephoneNumber', defaultMessage: 'Operator Telephone Number', description: 'Label text' },
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft],
        defaultValue: ''
    },
    [AudioBridgeConnectionField.OPERATOR_PIN]: {
        labelDescriptor: { id: 'oe.connection.operatorPin', defaultMessage: 'Operator PIN', description: 'Label text' },
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft],
        defaultValue: ''
    },
    [AudioBridgeConnectionField.OPERATOR_CONSOLE_LINK]: {
        labelDescriptor: { id: 'oe.connection.operatorConsoleLink', defaultMessage: 'Operator Console Link', description: 'Label text' },
        validators: [plainText.maxLength.medium, url.regExp],
        formatters: [trimSpaces],
        defaultValue: 'https://operator.turbobridge.com'
    },
    [AudioBridgeConnectionField.CONNECTION_NOTES]: {
        labelDescriptor: { id: 'oe.connection.connectionNotes', defaultMessage: 'Connection Notes', description: 'Label text' },
        validators: [plainText.maxLength.long],
        formatters: [trimSpacesLeft],
        defaultValue: ''
    },
    [AudioBridgeConnectionField.MEETING_UNIQUE_REFERENCE]: {
        label: 'Unique Reference',
        labelDescriptor: { id: 'oe.connection.uniqueReference', defaultMessage: 'Unique Reference', description: 'Label text' },
        valueOptions: [ { label: 'No Unique Reference', value: 0 }, 1, 2, 3, 4, 5 ],
    },
    [AudioBridgeConnectionField.AUTO_SYNC_ENABLED]: {
        labelDescriptor: { id: 'oe.connection.autoSyncEnabled', defaultMessage: 'Sync Meeting Attendee', description: 'Label text' },
        defaultValue: false,
    },
    // Carried through from the Create Audio Bridge modal so it can be reported in the
    // Mixpanel New connection event. Not surfaced as an editable field in this form.
    [AudioBridgeConnectionField.AUTOMATICALLY_RECORD_ENABLED]: {
        defaultValue: false,
    },
};

export const configList: FormVmItemConfigType[] = buildConfigList(FORM_CONFIG);
